import {uMath} from "src/components/Planner/planner/uMath";
import {uDev} from "src/components/Planner/planner/uDev";
import {SWI_PNID} from "src/components/Planner/planner/planner";

export class GroupPathFinder {

  /**
   * @function
   * @template T
   * @param {T[]} xs
   * @returns {T[][]}
   */
  perm2(xs) {
    var length = xs.length,
        result = [xs.slice()],
        c = new Array(length).fill(0),
        i = 1, k, p;

    while (i < length) {
      if (c[i] < i) {
        k = i % 2 && c[i];
        p = xs[i];
        xs[i] = xs[k];
        xs[k] = p;
        ++c[i];
        i = 1;
        result.push(xs.slice());
      } else {
        c[i] = 0;
        ++i;
      }
    }
    return result;
  }
  /**
   * @function
   * @template T
   * @param {T[]} xs
   * @returns {[T,T][]}
   */
  generateAllPairs(xs) {
    var pairs = [];

    for (var i = 0; i < xs.length - 1; i++) {
      for (var j = i + 1; j < xs.length; j++) {
        pairs.push([xs[i], xs[j]]);
      }
    }

    return pairs;
  }
  /**
   * Create a Map that contains the distances between device pairs
   * @param {[DeviceWithCenter, DeviceWithCenter][]} dwcPairs
   * @param {Room[]} rooms
   */
  generateDistCache(dwcPairs, rooms) {
    var cache = new Map();
    var roomPolygons = rooms.map(function (room) {
      return room.poly;
    });
    for (var i = 0; i < dwcPairs.length; i++) {
      var segment = uMath.segmentFromPoints(dwcPairs[i][0].center, dwcPairs[i][1].center);
      var dist = uMath.segmentLength(segment);

      if (uMath.segmentIntersectsAnyPolygon(segment, roomPolygons)) {
        dist *= 20;
      }

      cache.set(dwcPairs[i][1].idx + "_" + dwcPairs[i][0].idx, dist)
      cache.set(dwcPairs[i][0].idx + "_" + dwcPairs[i][1].idx, dist)
    }

    return cache;
  }
  /**
   *
   * @param {DeviceWithCenter[]} points
   * @param {Room[]} rooms
   * @param {boolean} canChangeFirstElement
   */
  bruteForceSalesman(points, canChangeFirstElement = false, rooms) {
    if (points.length <= 2) { return points; }

    var clonedPoints = points.map(function (pt, idx) {
      return { ...pt, idx };
    });

    if (canChangeFirstElement) {
      var perms = this.perm2(clonedPoints);
    }
    else {
      var startPt = clonedPoints[0];
      var otherPts = clonedPoints.slice(1, clonedPoints.length);
      var permsRaw = this.perm2(otherPts);
      var perms = [];
      for (var i = 0; i < permsRaw.length; i++) {
        permsRaw[i].unshift(startPt);
        perms.push(permsRaw[i]);
      }
    }

    var distCache = this.generateDistCache(this.generateAllPairs(clonedPoints), rooms);

    var minDist = 0;
    var minDistIdx = -1;

    for (var i = 0; i < perms.length; i++) {
      var dist = 0;
      for (var j = 1; j < perms[i].length; j++) {
        dist += distCache.get(perms[i][j - 1].idx + "_" + perms[i][j].idx);
      }
      if (minDistIdx === -1 || dist < minDist) {
        minDistIdx = i;
        minDist = dist;
      }
    }

    return perms[minDistIdx];
  }
  /**
   *
   * @param {DeviceWithCenter[]} points
   * @param {boolean} canChangeFirstElement
   * @param {Room[]} rooms
   * @returns {DeviceWithCenter[]}
   */
  greedySalesman(points, canChangeFirstElement = false, rooms) {
    if (points.length <= 2) { return points; }

    var roomPolygons = rooms.map(function (room) {
      return room.poly;
    });
    var triedPoints = canChangeFirstElement ? points.length : 1;

    var minOrder = null;
    var minDist = -1;
    var minRouteIntersects = -1;

    for (var i = 0; i < triedPoints; i++) {
      var clonedPoints = Object.assign([], points);

      var currentPoint = clonedPoints[i];
      var resultOrder = [currentPoint];
      clonedPoints.splice(i, 1);
      var routeIntersects = 0;

      while (clonedPoints.length > 0) {
        var minPtIdx = -1;
        var minPtDist = -1;
        var minPtIntersects = -1;
        for (var j = 0; j < clonedPoints.length; j++) {
          var nextPointCandidate = clonedPoints[j];
          var dist = uMath.pointDistance(currentPoint.center, nextPointCandidate.center);
          var intersects = +uMath.segmentIntersectsAnyPolygon(uMath.segmentFromPoints(currentPoint.center, nextPointCandidate.center), roomPolygons);
          if (minPtIdx === -1 || intersects < minPtIntersects || intersects === minPtIntersects && dist < minPtDist) {
            minPtIdx = j;
            minPtDist = dist;
            minPtIntersects = intersects;
          }
        }
        currentPoint = clonedPoints[minPtIdx];
        resultOrder.push(currentPoint);
        clonedPoints.splice(minPtIdx, 1);
        routeIntersects += minPtIntersects;
      }

      var lengthOfSolution = uMath.polyLineLength(resultOrder.map(function (pt) { return pt.center; }));

      if (minOrder === null || routeIntersects < minRouteIntersects || routeIntersects === minRouteIntersects && lengthOfSolution < minDist) {
        minOrder = resultOrder;
        minDist = lengthOfSolution;
        minRouteIntersects = routeIntersects;
      }
    }

    return minOrder;
  }
  /**
   *
   * @param {DeviceWithCenter[]} points
   * @param {boolean} canChangeFirstElement
   * @param {Room[]} rooms
   * @returns {DeviceWithCenter[]}
   */
  adaptiveSalesman(points, canChangeFirstElement = false, rooms) {
    if (points.length <= 2) {
      return points;
    }
    else if (points.length <= 8) {
      return this.bruteForceSalesman(points, canChangeFirstElement, rooms);
    }
    else {
      return this.greedySalesman(points, canChangeFirstElement, rooms);
    }
  }
  /**
   *
   * @param {DeviceWithCenter[]} outputsOrder
   * @param {Room[]} rooms
   */
  placeClosestOutputToWallFirst(outputsOrder, rooms) {
    const needsClosestPlaced = outputsOrder.some(output => output.device.product_name_id == 102 || output.device.product_name_id == 208);
    if (!needsClosestPlaced) { return false; }

    let minIdx = null;
    let minDist = null;

    outputsOrder.forEach((output, idx) => {
      const room = rooms.find(room => room.id == output.device.roomId);
      if (room) {
        const closestPt = uMath.pointClosestPtToPolygon(output.center, room.poly);
        const dist = uMath.pointDistance(closestPt, output.center);
        if (!minDist || dist < minDist) {
          minDist = dist;
          minIdx = idx;
        }
      }
    });

    if (minIdx) {
      const elem = outputsOrder.splice(minIdx, 1);
      outputsOrder.unshift(elem[0]);
    }

    return true;
  }
  /**
   *
   * @param {DeviceWithCenter[]} inputsOrder
   * @param {DeviceWithCenter[]} outputsOrder
   * @param {Room[]} rooms
   */
  correctInputsOutputsOrder(inputsOrder, outputsOrder, rooms, inputOrderIsFixed) {
    try {
      var roomPolies = rooms.map(function (room) { return room.poly; });

      if (inputsOrder.length >= 1 && outputsOrder.length >= 1) {
        const pairs = [];
        let minPair = null;
        let minPairDist = -1;
        let minPairIntersects = -1;
        var validInputIdxes = inputOrderIsFixed ? [inputsOrder.length - 1] : [0, inputsOrder.length - 1];
        validInputIdxes.forEach(function (inputIdx) {
          const currInput = inputsOrder[inputIdx];
          currInput.isInput = true;
          currInput.isFirst = inputIdx === 0;
          [0, outputsOrder.length - 1].forEach(function (outputIdx) {
            const currOutput = outputsOrder[outputIdx];
            currOutput.isOutput = true;
            currOutput.isLast = outputIdx === outputsOrder.length - 1;
            pairs.push([currInput, currOutput]);
          });
        });

        pairs.forEach(function (pair) {
          const dist = uMath.pointDistance(pair[0].center, pair[1].center);
          const intersects = uMath.segmentIntersectsWhichPolygons(uMath.segmentFromPoints(pair[0].center, pair[1].center), roomPolies).length;
          if (minPair === null || intersects < minPairIntersects || intersects === minPairIntersects && dist < minPairDist) {
            minPair = pair;
            minPairDist = dist;
            minPairIntersects = intersects;
          }
        });

        if (minPair[0].isFirst) { inputsOrder.reverse(); }
        if (minPair[1].isLast) { outputsOrder.reverse(); }
      }
    }
    catch (err) {
      console.log('got err while correcting group order', err);
    }
  }
  /**
   * Get the top-leftmost element among devices
   * @param {DeviceWithCenter[]} devicesWithCenters
   */
  getTopLeftElem(devicesWithCenters) {
    if (devicesWithCenters.length === 0) { return null; }
    var miny = devicesWithCenters[0].center.y, minx = devicesWithCenters[0].center.x;

    // top left first
    // get topleft bound
    for (var i = 1; i < devicesWithCenters.length; i++) {
      if (devicesWithCenters[i].center.x < minx) { minx = devicesWithCenters[i].center.x; }
      if (devicesWithCenters[i].center.y < miny) { miny = devicesWithCenters[i].center.y; }
    }

    var minDist = 0;
    var minIdx = -1;

    for (var i = 0; i < devicesWithCenters.length; i++) {
      var dist = uMath.pointDistance({ x: minx, y: miny }, devicesWithCenters[i].center);
      if (minIdx === -1 || dist < minDist) {
        minDist = dist;
        minIdx = i;
      }
    }

    return devicesWithCenters[minIdx];
  }
  /**
   *
   * @param {DeviceWithCenter[]} otherSwitches
   * @param {DeviceWithCenter | undefined} firstSwitch
   * @param {DeviceWithCenter | undefined} lastSwitch
   * @param {Room[]} rooms
   * @returns {DeviceWithCenter[]}
   */
  inputsAdaptiveTSP(otherSwitches, firstSwitch, lastSwitch, rooms) {
    if (otherSwitches.length <= 7) {
      return this.inputsBruteForceTSP(otherSwitches, firstSwitch, lastSwitch, rooms);
    }
    else {
      return this.inputsGreedyTSP(otherSwitches, firstSwitch, lastSwitch, rooms);
    }
  }
  /**
   *
   * @param {DeviceWithCenter[]} otherSwitches
   * @param {DeviceWithCenter | undefined} firstSwitch
   * @param {DeviceWithCenter | undefined} lastSwitch
   * @param {Room[]} rooms
   * @returns {DeviceWithCenter[]}
   */
  inputsBruteForceTSP(otherSwitches, firstSwitch, lastSwitch, rooms) {
    var allSwitches = otherSwitches.concat([firstSwitch, lastSwitch].filter(function (elem) { return !!elem }));
    var switchPairs = this.generateAllPairs(allSwitches);

    // filter out firstSwitch and lastSwitch if they don't exist
    var distCache = this.generateDistCache(switchPairs, rooms);

    // all permutations of the devs in the middle
    var perms = this.perm2(otherSwitches);

    // append first and last dev at the beginning and end (if they exist)
    for (var i = 0; i < perms.length; i++) {
      if (firstSwitch) { perms[i].unshift(firstSwitch); }
      if (lastSwitch) { perms[i].push(lastSwitch); }
    }

    var minDist = 0;
    var minDistIdx = -1;

    // get minimal cost permutation
    for (var i = 0; i < perms.length; i++) {
      var dist = 0;
      for (var j = 1; j < perms[i].length; j++) {
        dist += distCache.get(perms[i][j - 1].idx + "_" + perms[i][j].idx);
      }
      if (minDistIdx === -1 || dist < minDist) {
        minDistIdx = i;
        minDist = dist;
      }
    }

    return perms[minDistIdx];
  }
  /**
   *
   * @param {DeviceWithCenter[]} otherSwitches
   * @param {DeviceWithCenter | undefined} firstSwitch
   * @param {DeviceWithCenter | undefined} lastSwitch
   * @param {Room[]} rooms
   * @returns {DeviceWithCenter[]}
   */
  inputsGreedyTSP(otherSwitches, firstSwitch, lastSwitch, rooms) {
    var current = firstSwitch;
    var leftOvers = otherSwitches.slice(0);

    if (!current && leftOvers.length > 0) {
      current = leftOvers[0];
      leftOvers = leftOvers.slice(1, leftOvers.length);
    }

    if (!current) { return []; }

    var newOrder = [current];

    while (leftOvers.length > 0) {
      var minIdx = -1;
      var minDist = -1;
      for (var i = 0; i < leftOvers.length; i++) {
        var currDist = uMath.pointDistance(current.center, leftOvers[i].center);
        if (minIdx === -1 || currDist < minDist) {
          minDist = currDist;
          minIdx = i;
        }
      }
      current = leftOvers[minIdx];
      newOrder.push(current);
      leftOvers.splice(minIdx, 1);
    }
    if (lastSwitch) {
      newOrder.push(lastSwitch);
    }

    return newOrder;
  }
  /**
   *
   * @param {DeviceWithCenter[]} route
   */
  routeLength(route) {
    var length = 0;
    for (let i = 1; i < route.length; i++) {
      length += uMath.pointDistance(route[i - 1].center, route[i].center);
    }
    return length;
  }
  /**
   * Devices will be connected like [Inputs] - [Outputs] eg. rocker(106) - intermediate(106+6) - rocker(106) - light - light - ...
   * @param {Device[]} groupDevices
   * @param {Room[]} rooms
   * @returns {DeviceWithCenter[]}
   */
  groupSalesman(groupDevices, rooms) {
    var devsWithCenters = groupDevices.map(function (dev, idx) {
      var center = { x: dev.x, y: dev.y };
      if (typeof uDev === "object" && uDev.getCenterOfDevice_NoDOM) {
        center = uDev.getCenterOfDevice_NoDOM(dev.deviceId);
      }
      else if ('center' in dev) {
        center = { x: dev.center.x, y: dev.center.y };
      }
      return {
        device: dev,
        center: center,
        input: dev.input,
        idx: idx
      };
    });
    if (devsWithCenters.length <= 2) { // trivial solution
      if (devsWithCenters.length === 2 && !devsWithCenters[0].input && devsWithCenters[1].input) {
        devsWithCenters.reverse();
      }
      return devsWithCenters;
    }

    // separate devs by type
    /**
     * @type {DeviceWithCenter[]}
     */
    var inputs = [];
    /**
     * @type {DeviceWithCenter[]}
     */
    var outputs = [];

    for (var i = 0; i < devsWithCenters.length; i++) {
      var curr = devsWithCenters[i];
      if (curr.device.input) {
        inputs.push(curr);
      }
      else {
        outputs.push(curr);
      }
    }

    // separate inputs by basic and others
    /**
     * @type {DeviceWithCenter[]}
     */
    var inputs_106 = [];
    /**
     * @type {DeviceWithCenter[]}
     */
    var inputs_others = [];
    inputs.forEach(function (input) {
      if (uMath._does([
        SWI_PNID.twopole_102,
        SWI_PNID.switch_106,
        SWI_PNID.double_106p6,
        SWI_PNID.onepole_101
      ])
      .contain(input.device.product_name_id)) {
        inputs_106.push(input);
      }
      else {
        inputs_others.push(input);
      }
    });

    // these may not exist, but if there are two basic switches, they should be at the beginning and the end of the line
    var firstSwitch = inputs_106[0];
    var lastSwitch = inputs_106[1];
    if (firstSwitch && lastSwitch &&
        firstSwitch.device.product_name_id != SWI_PNID.double_106p6 &&
        lastSwitch.device.product_name_id == SWI_PNID.double_106p6) {
      var temp = lastSwitch;
      lastSwitch = firstSwitch;
      firstSwitch = temp;
    }
    inputs_others = inputs_others.concat(inputs_106.splice(2, inputs_106.length));
    var inputOrderIsFixed = firstSwitch && lastSwitch && firstSwitch.device.product_name_id != lastSwitch.device.product_name_id;

    // get the order of the inputs
    var inputsOrder = this.inputsAdaptiveTSP(inputs_others, firstSwitch, lastSwitch, rooms);

    if (firstSwitch || lastSwitch) {
      outputs.forEach(function (output) {
        var d = -1;
        if (firstSwitch) {
          var dist = uMath.pointDistance(firstSwitch.center, output.center);
          if (d === -1 || dist < d) {
            d = dist;
          }
        }
        if (lastSwitch) {
          var dist = uMath.pointDistance(lastSwitch.center, output.center);
          if (d === -1 || dist < d) {
            d = dist;
          }
        }
        output.minDistFromSwitch = d;
      });
      outputs.sort(function (a, b) {
        return a.minDistFromSwitch - b.minDistFromSwitch;
      });
    }


    if (outputs.length && !inputs.length) {
      //var isLedStripe = this.placeClosestOutputToWallFirst(outputs, rooms);
      //var canReplaceFirstElem = !isLedStripe;
      //return this.adaptiveSalesman(outputs, canReplaceFirstElem, rooms);
      return this.adaptiveSalesman(outputs, false, rooms);
    }
    else {
      // solve salesman for outputs starting with the selected starting point
      var outputsOrder = this.adaptiveSalesman(outputs, false, rooms);
      this.correctInputsOutputsOrder(inputsOrder, outputsOrder, rooms, inputOrderIsFixed);
      var route1 = inputsOrder.concat(outputsOrder);

      var outputsOrder = this.adaptiveSalesman(outputs, true, rooms);
      this.correctInputsOutputsOrder(inputsOrder, outputsOrder, rooms, inputOrderIsFixed);
      var route2 = inputsOrder.concat(outputsOrder);

      return this.routeLength(route1) < this.routeLength(route2) ? route1 : route2;
    }
  }
}